// This component is responsible for pagination

import React, { forwardRef, useCallback, useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import {
  accessors,
  CARD_INVERT_PASTILE_COLOR_ON_EVENTS,
  formatTimeDayMonthYear,
  isURL,
} from "../../utils";

export const CardCollection = ({
  associationColor,
  cardsPerPage,
  className = "",
  currentPage,
  data,
  onCardHover = () => {},
  onViewEnd = () => {},
}) => {
  // Used to check if item is in view
  const { ref: lastItem, inView: lastItemInView } = useInView({
    threshold: 0,
  });
  const containerRef = useRef();

  const firstItemIdx = 0;
  const slicedData = data.slice(firstItemIdx, currentPage * cardsPerPage);

  useEffect(() => {
    lastItemInView && onViewEnd();
  }, [lastItemInView, onViewEnd]);

  useEffect(() => {
    // when new data comes in, scroll to Top
    if (!containerRef.current) return;
    containerRef.current.scrollTo(0, 0);
  }, [data]);

  return (
    <div ref={containerRef} className={`${className}`}>
      {slicedData.map((d, idx) => (
        <Card
          key={`card-row-${data.id || idx}`}
          associationColor={associationColor}
          className="block-font"
          data={d}
          onHover={onCardHover}
          ref={idx === slicedData.length - 1 ? lastItem : null}
          index={idx + 1}
        />
      ))}
    </div>
  );
};

export const Card = forwardRef(
  (
    {
      className = "",
      data,
      index,
      onHover = () => {},
      associationColor = (d) => `transparent`,
      style = {},
      noBorder = false,
    },
    ref
  ) => {
    const onHoverCb = useCallback(
      (e) => {
        // stop event bubbling to children
        e.stopPropagation();
        e.preventDefault();

        if (e.target.dataset.id) onHover(e);
      },
      [onHover]
    );

    // const multipleCountries =
    //   Array.isArray(accessors.association(data)) &&
    //   accessors.association(data)?.length > 1;
    const multipleCountries = accessors.allAssociations(data)?.length > 1;
    const dateFuzziness = accessors.date.fuzziness(data);
    const countries = multipleCountries
      ? accessors.allAssociations(data).join(" · ")
      : accessors.association(data);

    let targetNames = accessors.target.name(data);
    const targets = Array.isArray(targetNames)
      ? targetNames.join("<br />")
      : targetNames;

    let targetAssociations = accessors.target.association(data);
    targetAssociations = Array.isArray(targetAssociations)
      ? targetAssociations.join("<br />")
      : targetAssociations;

    const sources = accessors.sources(data);
    const sourceURLs = sources.filter((d) => isURL(d));
    const sourceTexts = sources.filter((d) => !isURL(d));

    const eventType = accessors.event.type(data);

    const invertColorOnPastile =
      CARD_INVERT_PASTILE_COLOR_ON_EVENTS.includes(eventType);

    return (
      <div
        onMouseEnter={onHoverCb}
        style={style}
        className={`card-stack__item p1 justify-between ${
          noBorder ? `` : `border-right`
        } ${className}`}
        ref={ref}
        data-id={accessors.id(data)}
      >
        {/* <div className="h3 w-60">{index}</div> */}
        <div className={`flex flex-column col-12 mt2`}>
          {/* <div className="flex flex-row flex-wrap mb1 h4 bold">
            <div className="mr1">
            {formatTimeDayMonthYear(accessors.date.start(data))}{" "}
            </div>
            <div>
            {accessors.event.place(data) && ` — `}
            {accessors.event.place(data)}
            </div>
          </div> */}
          <div className="flex flex-row flex-wrap mb1 bold items-start px1">
            {/* <div className="h3 width-6ch bold">{data.id}/</div> */}
            {/* <Pastile
              className="p1 mr2"
              backgroundColor="var(--white)"
              color="var(--black)"
              text={String(data.id).padStart(3, "0")}
            /> */}
            <Pastile
              borderColor={`transparent`}
              backgroundColor={associationColor(data)}
              color={
                invertColorOnPastile
                  ? `var(--primary-color)`
                  : `var(--secondary-color)`
              }
              className="uppercase p1 h6"
              text={eventType}
            />
            <div
              className={`${
                multipleCountries ? `ml2 flex flex-row flex-wrap col-6` : `ml2`
              }`}
            >
              <div className="color-gray h5">
                <div className="">{countries}</div>
                <div className="flex flex-row">
                  <div className="mr1">
                    {formatTimeDayMonthYear(accessors.date.start(data))}
                  </div>
                  <div className="italic">
                    {dateFuzziness > 0 && <div>±{dateFuzziness} days</div>}
                  </div>
                </div>
              </div>
              <div className="col-12"></div>
              <div className="mt1 color-secondary h5">
                <div
                  dangerouslySetInnerHTML={{
                    __html: `${targets || targetAssociations || ``}`,
                  }}
                ></div>
              </div>
            </div>
            {/* <div>{data.link}</div> */}
          </div>
          <div className="h3 mt1 px1 mb2 lh-sm pr1">{data.description}</div>
          <div className="sources h4 color-gray pr1 px1 mb2">
            <div className="bold">
              Source{sourceURLs.length + sourceTexts.length > 1 ? `s` : null}
            </div>
            {/* <div className="italic"> */}
            <ul>
              {sourceURLs.map((url, idx) => (
                <li className="underline" key={`source-url-${idx}`}>
                  <a href={url} target="__blank">
                    {url.slice(0, 30)}...
                  </a>
                </li>
              ))}
              {sourceTexts.map((text, idx) => (
                <li key={`source-text-${idx}`}>{text}</li>
              ))}
            </ul>
          </div>
          {/* </div> */}
        </div>
      </div>
    );
  }
);

const Pastile = ({
  borderColor = `var(--secondary-color)`,
  color = `var(--secondary-color)`,
  backgroundColor = `initial`,
  text = ``,
  className = ``,
}) => (
  <div
    className={`rounded-lg border pointer ${className}`}
    style={{
      backgroundColor,
      borderColor,
      color,
    }}
  >
    <div>{text}</div>
  </div>
);
